
import { defineStore } from 'pinia'
import { get, throttle } from 'es-toolkit/compat'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useRootStore } from '~/store/root'
import AnalyticsService from '~/libs/analytics-service'
import env from '~/libs/env'
import appConstants from '~/libs/constants/application-constants.js'

const { nuxtBaseUrl } = appConstants

export const useUserStore = defineStore('user', () => {
  const { $axios, $lendioCookies } = useNuxtApp()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const rootStore = useRootStore()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const accessToken = ref(null)
  const signInError = ref(false)
  const freshLogin = ref(null)

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/

  const hasOptimusUser = computed(() => {
    return Boolean(get(rootStore.authUser, 'id'))
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  async function setAccessToken(payload) {
    if (
      (payload && await $lendioCookies.get('accessToken') !== payload) ||
      !accessToken.value
    ) {
      await $lendioCookies.set('accessToken', payload, {
        path: `${nuxtBaseUrl}/`,
        expires: 0.5,
      })
      accessToken.value = payload
    }
  }

  function reset() {
    accessToken.value = null
    signInError.value = false
    freshLogin.value = null
  }

  function getAuthHeader({ acceptNoAuth = false, useCookies = false, silent = true, requestUrl = null } = {}) {
    let cookieToken = $lendioCookies.get('token')
    let token = rootStore.authToken

    if (token) {
      return `lendio-jwt ${token}`
    }

    if (useCookies && cookieToken) {
      return `lendio-jwt ${cookieToken}`
    }

    if (acceptNoAuth) {
      return
    }

    if (!silent) {
      log.error('Authorization from getAuthHeader is undefined', {
        requestUrl,
        acceptNoAuth,
        useCookies,
        process: 'client',
      })
    }

    return false
  }

  async function setFreshLogin(payload) {
    if (payload) {
      freshLogin.value = payload
      await $lendioCookies.set('freshLogin', payload)
    } else {
      freshLogin.value = null
      await $lendioCookies.remove('freshLogin')
    }
  }

  const SESSION_REFRESH_THROTTLE_SECONDS = 60
  let refreshingSessionInProgress = false

  async function queueSessionExtension() {
    if (!this.getAuthHeader()) {
      return
    }

    // If unable to access expiry from current token, refresh immediately
    const tokenExpiry = rootStore.getAuthTokenExpiry()
    const expiresFromNowSeconds = tokenExpiry ? tokenExpiry - Date.now() / 1000 : null
    if (!expiresFromNowSeconds) {
      return this.refreshSession()
    }

    // Early return if still more than 15 minutes until expiry
    const expiresFromNowMinutes = expiresFromNowSeconds / 60
    const refreshWithinMinutes = 15
    if(expiresFromNowMinutes > refreshWithinMinutes) {
      return
    }

    // Otherwise, let the throttle handle duplicated requests
    this.safeRefreshSession()
  }

  async function refreshSession() {
    if (!this.getAuthHeader()) {
      return
    }

    if (refreshingSessionInProgress) {
      return
    }
    refreshingSessionInProgress = true

    const newToken = await $axios
      .get(`${env('identityServiceUrl')}/auth/token-refresh`)
      .then((res) => {
        if (res && res.data && res.data.error) {
          log.error('[IDENT] Failed to refresh token (then block)', res.data.error)
          return
        }
        return res && res.data && res.data.data
      })
      .catch((err) => {
        if (
          err.status === 403 ||
          err.status === 401 ||
          err.response?.data?.statusCode === 401 ||
          err.response?.data?.statusCode === 403
        ) {
          return
        }
        log.error('[IDENT] Failed to refresh token (catch block)', err)
        return
      })

    if (newToken) {
      await rootStore.setAuthToken(newToken)
    }
    refreshingSessionInProgress = false
  }

  /**
   * This action is fired on every mount of a layout. It should be quick,
   * and it's a catch-all for login tracking.
   */
  async function loadFreshLogin() {
    const login = freshLogin.value ?? await $lendioCookies.get('freshLogin')
    if (login == 'login') {
      await AnalyticsService.trackLogin($lendioCookies)
    }
    this.setFreshLogin(null)
  }

  async function extractUserProfile(route) {
    const hash = get(route, 'hash', null)

    if (
      !hash ||
      hash === '#' ||
      accessToken.value ||
      rootStore.isLoggedIn ||
      rootStore.authUser
    ) {
      return false
    }

    log.warning('Deprecated code: extractUserProfile found a hash', { hashSnippet: hash.substr(0, 6) + '...' })

    const parsedHash = hash
      .substr(1)
      .split('&')
      .reduce((carry, item) => {
        let [key, val] = item.split('=')
        carry[key] = val
        return carry
      }, {})

    if (!parsedHash.access_token || !parsedHash.id_token) {
      return false
    }

    await this.setAccessToken(parsedHash.access_token)
    route.hash = ''
    route.fullPath = route.path
    route.href = route.path

    return route
  }

  async function reauth() {
    // don't attempt to reauth if there is no auth token
    if (!rootStore.authToken) {
      return false;
    }

    return this.queueSessionExtension()
  }

  return {
    //STATE
    accessToken,
    signInError,
    freshLogin,
    //GETTERS
    hasOptimusUser,
    //ACTIONS
    setAccessToken,
    queueSessionExtension,
    refreshSession,
    safeRefreshSession: throttle(
      refreshSession,
      SESSION_REFRESH_THROTTLE_SECONDS * 1000,
      {
        leading: true,
        trailing: false,
      }
    ),
    getAuthHeader,
    setFreshLogin,
    loadFreshLogin,
    extractUserProfile,
    reauth,
    reset,
  }
})
